import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaUserGraduate,
  FaBookOpen,
  FaAward,
  FaVideo,
  FaSignOutAlt,
  FaChevronDown,
  FaPlayCircle,
  FaCheckCircle,
  FaLock,
} from 'react-icons/fa';
import { getFirstName } from '../utils/nameUtils';

const API_BASE_URL = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' && window.location.hostname.includes('localhost') ? 'http://localhost:5000/api' : '/api');

export const StudentProfileDropdown = ({ currentUser, onLogout }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState('courses'); // 'courses' | 'diplomas' | 'seminars'
  const [loading, setLoading] = useState(false);
  const [myLearningData, setMyLearningData] = useState({ courses: [], diplomas: [], seminars: [] });
  const dropdownRef = useRef(null);

  const studentEmail = localStorage.getItem('datx_user_email') || currentUser?.email;

  const fetchMyLearning = async () => {
    if (!studentEmail) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/registrations/my-learning?email=${encodeURIComponent(studentEmail)}`);
      if (res.ok) {
        const data = await res.json();
        setMyLearningData(data);
      }
    } catch (err) {
      console.error('Error fetching student registrations:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchMyLearning();
    }
  }, [isOpen]);

  // Click outside listener to auto-close
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const totalRegisteredItems =
    (myLearningData.courses?.length || 0) +
    (myLearningData.diplomas?.length || 0) +
    (myLearningData.seminars?.length || 0);

  return (
    <div className="relative font-arabic dir-rtl" ref={dropdownRef}>
      {/* Topbar Profile Trigger Button */}
      <motion.button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="قائمة حساب الطالب"
        aria-expanded={isOpen}
        aria-haspopup="true"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className="flex items-center gap-2.5 bg-[#00131D] hover:bg-slate-900 text-white px-4 py-2 rounded-xl border border-blue-500/30 text-xs font-bold shadow-md transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-400"
      >
        <span aria-hidden="true" className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse"></span>
        <div className="flex items-center gap-1.5">
          <FaUserGraduate aria-hidden="true" className="text-blue-400 text-sm" />
          <span>أهلاً: <strong className="text-white">{getFirstName(currentUser?.name)}</strong></span>
        </div>
        <FaChevronDown aria-hidden="true" className={`text-[10px] text-blue-300 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
      </motion.button>

      {/* Animated Dropdown Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute left-0 mt-3 w-80 md:w-96 bg-[#031B2E] text-white rounded-2xl shadow-2xl border border-white/10 overflow-hidden z-50 text-right dir-rtl"
            style={{ boxShadow: '0 20px 50px rgba(0, 0, 0, 0.5)' }}
          >
            {/* Header */}
            <div className="bg-[#00131D] text-white p-4 border-b border-white/10 flex items-center justify-between">
              <div>
                <h4 className="font-bold text-sm text-white">{currentUser?.name}</h4>
                <span className="text-[11px] text-blue-200/60 block">{studentEmail}</span>
              </div>
              <span className="px-2.5 py-1 bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 rounded-full text-[10px] font-bold">
                طالب معتمد
              </span>
            </div>

            {/* Content Filter Tabs Inside Dropdown */}
            <div role="tablist" aria-label="أقسام التعلم" className="flex items-center justify-around bg-black/40 p-1.5 border-b border-white/10 text-xs font-bold">
              <button
                type="button"
                role="tab"
                aria-selected={activeSubTab === 'courses'}
                onClick={() => setActiveSubTab('courses')}
                className={`flex-1 py-1.5 rounded-lg text-center transition-all flex items-center justify-center gap-1 ${
                  activeSubTab === 'courses' ? 'bg-blue-600 text-white shadow-sm' : 'text-blue-200/60 hover:text-white'
                }`}
              >
                <FaBookOpen aria-hidden="true" className="text-[10px]" />
                <span>دوراتي ({myLearningData.courses?.length || 0})</span>
              </button>

              <button
                type="button"
                role="tab"
                aria-selected={activeSubTab === 'diplomas'}
                onClick={() => setActiveSubTab('diplomas')}
                className={`flex-1 py-1.5 rounded-lg text-center transition-all flex items-center justify-center gap-1 ${
                  activeSubTab === 'diplomas' ? 'bg-blue-600 text-white shadow-sm' : 'text-blue-200/60 hover:text-white'
                }`}
              >
                <FaAward aria-hidden="true" className="text-[10px]" />
                <span>دبلوماتي ({myLearningData.diplomas?.length || 0})</span>
              </button>

              <button
                type="button"
                role="tab"
                aria-selected={activeSubTab === 'seminars'}
                onClick={() => setActiveSubTab('seminars')}
                className={`flex-1 py-1.5 rounded-lg text-center transition-all flex items-center justify-center gap-1 ${
                  activeSubTab === 'seminars' ? 'bg-blue-600 text-white shadow-sm' : 'text-blue-200/60 hover:text-white'
                }`}
              >
                <FaVideo aria-hidden="true" className="text-[10px]" />
                <span>ندواتي ({myLearningData.seminars?.length || 0})</span>
              </button>
            </div>

            {/* List Body */}
            <div className="max-h-72 overflow-y-auto p-3 space-y-2 bg-[#011424]/60">
              {loading ? (
                <div className="py-8 text-center text-blue-200/60 text-xs">
                  <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                  جاري جلب قائمة تسجيلاتك المعتمدة...
                </div>
              ) : activeSubTab === 'courses' ? (
                /* COURSES LIST */
                myLearningData.courses.length === 0 ? (
                  <div className="p-6 text-center text-blue-200/60 text-xs space-y-2">
                    <FaLock className="w-8 h-8 text-blue-400/30 mx-auto" />
                    <p className="font-semibold text-white">لا توجد دورات مسجل فيها حالياً</p>
                    <p className="text-[10px] text-blue-200/50">تصفح قائمة الدورات واشترك للحصول على صلاحية الوصول.</p>
                  </div>
                ) : (
                  myLearningData.courses.map((course) => (
                    <div
                      key={course.id}
                      className="bg-white/5 p-3 rounded-xl border border-white/10 hover:border-blue-500/30 transition-all flex items-center justify-between gap-2"
                    >
                      <div className="truncate">
                        <h5 className="font-bold text-xs text-white truncate">{course.title}</h5>
                        <span className="text-[10px] text-emerald-400 font-semibold flex items-center gap-1 mt-0.5">
                          <FaCheckCircle className="text-[9px]" /> مسجل ومعتمد (APPROVED)
                        </span>
                      </div>

                      <button
                        onClick={() => {
                          setIsOpen(false);
                          navigate(`/courses/${course.id}/learn`);
                        }}
                        className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-[10px] font-bold flex items-center gap-1 shadow-sm flex-shrink-0 cursor-pointer"
                      >
                        <FaPlayCircle /> دخول LMS
                      </button>
                    </div>
                  ))
                )
              ) : activeSubTab === 'diplomas' ? (
                /* DIPLOMAS LIST */
                myLearningData.diplomas.length === 0 ? (
                  <div className="p-6 text-center text-blue-200/60 text-xs space-y-2">
                    <FaAward className="w-8 h-8 text-blue-400/30 mx-auto" />
                    <p className="font-semibold text-white">لا توجد دبلومات مسجل فيها حالياً</p>
                  </div>
                ) : (
                  myLearningData.diplomas.map((dip) => (
                    <div key={dip.id} className="bg-white/5 p-3 rounded-xl border border-white/10 flex items-center justify-between gap-2">
                      <div className="truncate">
                        <h5 className="font-bold text-xs text-white truncate">{dip.title}</h5>
                        <span className="text-[10px] text-emerald-400 font-semibold block mt-0.5">مسجل ومعتمد</span>
                      </div>
                      <button
                        onClick={() => {
                          setIsOpen(false);
                          navigate(`/tracks/${dip.id}`);
                        }}
                        className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-[10px] font-bold flex-shrink-0 cursor-pointer"
                      >
                        عرض المسار
                      </button>
                    </div>
                  ))
                )
              ) : (
                /* SEMINARS LIST */
                myLearningData.seminars.length === 0 ? (
                  <div className="p-6 text-center text-blue-200/60 text-xs space-y-2">
                    <FaVideo className="w-8 h-8 text-blue-400/30 mx-auto" />
                    <p className="font-semibold text-white">لا توجد ندوات مسجل فيها حالياً</p>
                  </div>
                ) : (
                  myLearningData.seminars.map((sem) => (
                    <div key={sem.id} className="bg-white/5 p-3 rounded-xl border border-white/10 space-y-2">
                      <div className="flex items-center justify-between">
                        <h5 className="font-bold text-xs text-white truncate">{sem.title}</h5>
                        <span className="text-[9px] bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded font-bold border border-emerald-500/30">
                          حضور معتمد
                        </span>
                      </div>
                      {sem.zoomLink ? (
                        <a
                          href={sem.zoomLink}
                          target="_blank"
                          rel="noreferrer"
                          className="w-full py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-[10px] font-bold text-center flex items-center justify-center gap-1.5"
                        >
                          <FaVideo className="text-[10px]" />
                          <span>رابط الزوم المباشر (Zoom)</span>
                        </a>
                      ) : (
                        <span className="text-[10px] text-blue-200/50 block">رابط الزوم يتفعل قبل الندوة بـ 30 دقيقة.</span>
                      )}
                    </div>
                  ))
                )
              )}
            </div>

            {/* Dropdown Footer: Logout Button */}
            <div className="p-3 bg-black/40 border-t border-white/10 flex items-center justify-between text-xs">
              <span className="text-[10px] text-blue-200/60">إجمالي تسجيلاتك: {totalRegisteredItems} محتوى</span>
              <button
                onClick={() => {
                  setIsOpen(false);
                  onLogout();
                }}
                className="px-3 py-1.5 bg-red-500/15 hover:bg-red-500/25 border border-red-500/30 text-red-400 rounded-lg text-[11px] font-bold transition-colors flex items-center gap-1 cursor-pointer"
              >
                <FaSignOutAlt className="text-xs" />
                <span>تسجيل الخروج</span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
